"use client"

import { AUTH_TOKEN } from "constants/AuthConstant"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setSupplementsState } from "store/slices/supplements"
import { isAuth } from "./isAuth"
import { setAuthUser, signOutSuccess } from "store/slices/authSlice"
import { refreshAccessToken, isRefreshingToken } from "refreshToken"

const useSupplements = () => {
  const [Supplements, setSupplements] = useState(null)
  const [Supplement, setSupplement] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const BASE_URL = process.env.REACT_APP_API_URL;

  const url_getAllSupplements = `${BASE_URL}/supplements`;
  const url_addSupplement = `${BASE_URL}/supplements`;
  const url_updateSupplement = `${BASE_URL}/supplements`;

  const dispatch = useDispatch()
  const tokenFromState = useSelector((state) => state.auth.token)

  if (localStorage.getItem(AUTH_TOKEN) != tokenFromState) {
    localStorage.removeItem(AUTH_TOKEN)
    dispatch(signOutSuccess())
    dispatch(setAuthUser({}))
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN)

    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken()
    }

    if (!token) {
      throw new Error("Authentication required")
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken()
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN)
        dispatch(signOutSuccess())
        dispatch(setAuthUser({}))
        if (navigate) navigate("/login")
        throw new Error("Session expired")
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    }

    return response
  }

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN)
      dispatch(signOutSuccess())
      dispatch(setAuthUser({}))
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur")
      return
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0])
      return
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message)
      return
    }

    if (errorData?.message) {
      setError(errorData.message)
      return
    }

    setError("Erreur inconnue")
  }

  const addSupplement = async (data, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        url_addSupplement,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setSupplement(responseData)
      await getAllSupplements(navigate)
    } catch (err) {
      console.error("Failed to add supplement", err)
      setError(err.message || "Failed to add supplement")
    } finally {
      setIsLoading(false)
    }
  }

  const getAllSupplements = async (navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_getAllSupplements, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseText = await response.text()
      const data = JSON.parse(responseText || "{}")

      if (Array.isArray(data.data)) {
        setSupplements(data.data)
        dispatch(setSupplementsState(data.data))
      } else {
        console.error("❌ Les suppléments ne sont pas un tableau :", data)
        setError("Les données des suppléments sont invalides.")
      }
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des suppléments :", err)
      setError(err.message || "Failed to get supplements")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteSupplement = async (id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_addSupplement}/${id}`, { method: "DELETE" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      setSupplement([])
      await getAllSupplements(navigate)
    } catch (err) {
      console.error("Failed to delete supplement", err)
      setError(err.message || "Failed to delete supplement")
    } finally {
      setIsLoading(false)
    }
  }

  const updateSupplement = async (data, id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        `${url_updateSupplement}/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setSupplement(responseData)
      await getAllSupplements(navigate)
    } catch (err) {
      console.error("Failed to update supplement", err)
      setError(err.message || "Failed to update supplement")
    } finally {
      setIsLoading(false)
    }
  }

  const getSupplementById = async (id, navigate) => {
    if (!id) return null

    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllSupplements}/${id}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      const responseData = await response.json()
      setSupplement(responseData.data || responseData)
      return responseData.data || responseData
    } catch (err) {
      console.error("Échec de la récupération", err)
      setError(err.message || "Échec de la récupération")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getSupplementById,
    deleteSupplement,
    updateSupplement,
    addSupplement,
    Supplement,
    Supplements,
    getAllSupplements,
    setSupplements,
    setSupplement,
    isLoading,
    error,
    setError,
  }
}

export default useSupplements